import React, { useEffect, useState } from 'react';
import { PageTemplate } from '../../layout/PageTemplate/PageTemplate';
import './Analytics.scss';
import { useWebsiteContext } from '../../context/WebsiteContext';
import useSWR from 'swr';
import { Skeleton } from 'primereact/skeleton';
import { KpiCard, KpiProps } from '../../components/KpiCard/KpiCard';
import { PrimeIcons } from 'primereact/api';
import { useClient } from '../../hooks/useClient';
import { Chart } from 'primereact/chart';
import { Card } from 'primereact/card';
import { aggregatedData as barAggregatedData, lineAggregatedData, pieAggregatedData } from '../../utils/chart-utils';
import { useLocaleContext } from '../../context/LocaleContext';
import { useOrganizationContext } from '../../context/OrganizationContext';
import { fetchWithCache } from '../../utils/cacheUtils';
import { useThemeContext } from '../../context/ThemeContext';

interface ChartDataSet {
  type: 'bar' | 'line' | 'pie' | 'doughnut';
  data: any;
  chartTitle: string;
  className?: string;
}

// 4 hours
const CACHE_TTL = 4 * 60 * 60 * 1000;

const Analytics = () => {
  const { organizationLoading } = useOrganizationContext();
  const { website, websitesLoading } = useWebsiteContext();
  const { locale } = useLocaleContext();
  const [dataForChart, setDataForChart] = useState<ChartDataSet[]>([]);
  const [kpiData, setKpiData] = useState<KpiProps[]>([]);
  const { get } = useClient();
  // The charts are built in render from CSS variables: a theme switch must render the page again.
  useThemeContext();

  const { data, isLoading } = useSWR(
    website ? `/websites/${website.id}/google-analytics?locale=${locale}` : null,
    (url: string) => fetchWithCache(url, () => get(url), CACHE_TTL),
  );

  useEffect(() => {
    if (data) {
      const chartsData: ChartDataSet[] = [
        {
          type: 'line',
          data: data.usersOverTime,
          chartTitle: 'Usuarios activos diarios ',
          className: 'large-chart',
        },
        {
          type: 'doughnut',
          data: data.sessionsByCategory,
          chartTitle: 'Origen de las sesiones',
          className: 'small-chart',
        },
        { type: 'bar', data: data.countryDistribution, chartTitle: 'Usuarios por país' },
        { type: 'bar', data: data.cityDistribution, chartTitle: 'Usuarios por ciudad' },
        { type: 'doughnut', data: data.deviceCategoryByUsers, chartTitle: 'Dispositivos' },
        { type: 'bar', data: data.sessionsBySource, chartTitle: 'Fuentes del tráfico' },
        { type: 'pie', data: data.browsersByUsers, chartTitle: 'Usuarios por navegador' },
        { type: 'bar', data: data.viewsByPage, chartTitle: 'Sesiones por página' },
      ];

      // TODO coger las variables desde css (usar blue-500, green-500, etc. de prime)
      const kpiTransformedData: KpiProps[] = [
        {
          value: data.activeUsersLast7Days?.value,
          previousPercentage: data.activeUsersLast7Days?.percentageComparedToLastWeek,
          title: 'Usuarios activos (7d)',
          icon: PrimeIcons.CHART_LINE,
          iconBkgColor: 'var(--info-chip)',
          iconBorderColor: 'var(--info-chip-border)',
          iconColor: 'var(--info)',
        },
        {
          value: data.newUsersLast7Days?.value,
          previousPercentage: data.newUsersLast7Days?.percentageComparedToLastWeek,
          title: 'Usuarios nuevos (7d)',
          icon: PrimeIcons.USER_PLUS,
          iconBkgColor: 'var(--success-chip)',
          iconBorderColor: 'var(--success-chip-border)',
          iconColor: 'var(--success)',
        },
        {
          value: data.engagedSessionsPercentageLast7Days ? `${data.engagedSessionsPercentageLast7Days.value}%` : undefined,
          previousPercentage: data.engagedSessionsPercentageLast7Days?.percentageComparedToLastWeek,
          title: 'Engagement rate (7d)',
          icon: PrimeIcons.THUMBS_UP,
          iconBkgColor: 'var(--warning-chip)',
          iconBorderColor: 'var(--warning-chip-border)',
          iconColor: 'var(--warning)',
        },
        {
          value: data.activeUsersYesterday?.value,
          previousPercentage: data.activeUsersYesterday?.percentageComparedToLastWeek,
          title: 'Usuarios activos ayer',
          icon: PrimeIcons.USERS,
          iconBkgColor: 'var(--accent-chip)',
          iconBorderColor: 'var(--accent-chip-border)',
          iconColor: 'var(--accent)',
        },
      ];

      setDataForChart(chartsData);
      setKpiData(kpiTransformedData);
    }
  }, [data]);

  const getDataToShow = (dataSet: ChartDataSet) => {
    // A failed Google Analytics call arrives as 200 with null fields.
    if (!dataSet.data) return null;
    switch (dataSet.type) {
      case 'line':
        return lineAggregatedData(dataSet.data);
      case 'bar':
        return barAggregatedData(dataSet.data);
      case 'pie':
        return pieAggregatedData(dataSet.data);
      case 'doughnut':
        return pieAggregatedData(dataSet.data);
    }
  };

  const loading = isLoading || organizationLoading || websitesLoading;

  return (
    <PageTemplate className='analytics-page' title='Analíticas'>
      <div className='kpi-container'>
        {!loading ? (
          kpiData.map((data: KpiProps, index) => <KpiCard {...data} key={index}></KpiCard>)
        ) : (
          <>
            <Skeleton />
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </>
        )}
      </div>

      <div className='charts-container'>
        {!loading ? (
          dataForChart.map((dataSet: ChartDataSet) => {
            const dataToShow = getDataToShow(dataSet);
            return (
              <Card key={dataSet.chartTitle} title={dataSet.chartTitle} className={dataSet.className}>
                <Chart type={dataSet.type} data={dataToShow?.chartData} options={dataToShow?.chartOptions} />
              </Card>
            );
          })
        ) : (
          <>
            <Skeleton className='large-chart' />
            <Skeleton className='small-chart' />
            <Skeleton />
            <Skeleton />
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </>
        )}
      </div>
    </PageTemplate>
  );
};

export default Analytics;
